import React from "react";
import { useAnketaI18n } from "src/internationalization";
import { CountUp } from "./motion";

/*
 * Trust band: hard numbers + credibility chips, placed right under the hero -
 * the single highest-leverage conversion element on competitor security sites
 * (Arctic Wolf's "10,000+ Kunden" + awards row, XM Cyber's stats). All copy is
 * i18n; numbers stay as literals.
 */

const tPrefix = "anketa.autonubil.home.trust";

interface Stat {
  value: string;
  labelKey: string;
  fallback: string;
}

// Honest, defensible capability facts (opsanio is sovereignty/compliance-first,
// not a customer-logo race): 20+ native integrations, 6+ frameworks mapped,
// fully on-prem, operated as a managed service.
const stats: Stat[] = [
  { value: "40+", labelKey: `${tPrefix}.stat.sources`, fallback: "Integrated systems" },
  { value: "6+", labelKey: `${tPrefix}.stat.frameworks`, fallback: "Compliance frameworks" },
  { value: "100%", labelKey: `${tPrefix}.stat.onprem`, fallback: "On-prem & sovereign" },
  // 24/7 "Managed SOC & SIEM" removed - opsanio is a SOC *foundation*, not a SOC.
  { value: "3", labelKey: `${tPrefix}.stat.models`, fallback: "Operating models" },
];

interface Badge {
  labelKey: string;
  fallback: string;
}

const badges: Badge[] = [
  { labelKey: `${tPrefix}.badge.iso`, fallback: "ISO 27001" },
  { labelKey: `${tPrefix}.badge.nis2`, fallback: "NIS2-ready" },
  { labelKey: `${tPrefix}.badge.bsi`, fallback: "BSI IT-Grundschutz" },
  { labelKey: `${tPrefix}.badge.gdpr`, fallback: "GDPR compliant" },
  { labelKey: `${tPrefix}.badge.tisax`, fallback: "TISAX" },
  { labelKey: `${tPrefix}.badge.hosting`, fallback: "Hosted in Germany" },
];

const ShieldIcon: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M9 12l2 2 4-4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const AutonubilTrustBand = (): JSX.Element => {
  const { translate } = useAnketaI18n();

  return (
    <div
      style={{
        background: "var(--ak-surface)",
        borderTop: "1px solid var(--ak-border)",
        borderBottom: "1px solid var(--ak-border)",
        padding: "44px clamp(24px, 5vw, 48px)",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* stats */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 24,
          }}
        >
          {stats.map((s) => (
            <div key={s.labelKey} style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "clamp(1.9rem, 3vw, 2.6rem)",
                  fontWeight: 800,
                  letterSpacing: "-0.03em",
                  lineHeight: 1,
                }}
              >
                <CountUp
                  value={s.value}
                  style={{
                    display: "inline-block",
                    background:
                      "linear-gradient(120deg, var(--ak-accent) 0%, var(--ak-accent-2) 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                />
              </div>
              <div
                style={{
                  marginTop: 8,
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: "var(--ak-text-secondary)",
                }}
              >
                {translate(s.labelKey, { defaultMessage: s.fallback } as any)}
              </div>
            </div>
          ))}
        </div>

        {/* credibility chips */}
        <div
          style={{
            marginTop: 36,
            paddingTop: 28,
            borderTop: "1px solid var(--ak-border-soft)",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "var(--ak-text-muted)",
              marginRight: 4,
            }}
          >
            {translate(`${tPrefix}.eyebrow`, {
              defaultMessage: "Compliance & certifications",
            } as any)}
          </span>
          {badges.map((b) => (
            <span
              key={b.labelKey}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                padding: "7px 14px",
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 600,
                color: "var(--ak-text-secondary)",
                background: "var(--ak-accent-tint)",
                border: "1px solid var(--ak-accent-border)",
              }}
            >
              <span style={{ color: "var(--ak-accent)", display: "inline-flex" }}>
                <ShieldIcon />
              </span>
              {translate(b.labelKey, { defaultMessage: b.fallback } as any)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AutonubilTrustBand;
